import { Row, Table as ReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { PosTableData } from 'src/tables/Table';

interface UseGetVirtuosoDataProps<T extends PosTableData> {
  table: ReactTable<T>;
}

export interface HeaderGroup<T extends PosTableData> {
  row: Row<T>;
  groupDepth: number;
}
interface VirtuosoGroupData<T extends PosTableData> {
  allSingleRows: Row<T>[]; // No grouped rows, just single rows with data
  rowHeaderGroups: HeaderGroup<T>[]; // Just grouped rows

  // Array of numbers where each number represents the amount of rows
  // in a group
  groupCounts: number[];
}

export const useGetVirtuosoGroups = <T extends PosTableData>({
  table,
}: UseGetVirtuosoDataProps<T>) => {
  // Only use it for getting the arrays of single rows and
  // the array of header rows
  const groupedTableRows = table.getRowModel().rows;
  const grouping = table.getState().grouping;

  return useMemo<VirtuosoGroupData<T>>(() => {
    const rowGroups: Row<T>[][] = [];
    const rowHeaderGroups: HeaderGroup<T>[] = [];
    const groupCounts: number[] = [];

    // Use it for accessing current rendering single row
    const allSingleRows: Row<T>[] = [];

    const map: Record<string, Row<T>[]> = {};
    for (const row of groupedTableRows) {
      // id: grouping row ID (when it's parent, parentId is undefined)
      // parentId: single row parent ID
      const { id, parentId, groupingColumnId } = row;

      const groupingId = parentId || id;
      map[groupingId] = map[groupingId] || [];
      const localRowGroups: Row<T>[] = map[groupingId];

      const isSingleRow = !row.getIsGrouped();

      // Only keep
      if (isSingleRow) {
        localRowGroups.push(row);
        allSingleRows.push(row);
      } else {
        const groupDepth = grouping.findIndex(
          (col) => col === groupingColumnId
        );
        rowHeaderGroups.push({ row, groupDepth });
      }

      if (!rowGroups.includes(localRowGroups)) {
        rowGroups.push(localRowGroups);
      }
    }

    for (const group of rowGroups) {
      groupCounts.push(group.length);
    }

    return { groupCounts, rowHeaderGroups, allSingleRows };
  }, [groupedTableRows, grouping]);
};
